/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import { createHydratedEntityState } from 'libs/ngrx/legacy/lib/entity-collection/data';

import type { ID, PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  AutomationTemplate,
  WorkflowAutomation,
  WorkflowAutomationAdditionalData,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

export const stageIdScope = (stageId: number) => scopeFactory('stageId', stageId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('PipelineTemplateStageAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchStages: (state, payload: { pipelineTemplateId: number }) =>
          adapter.markAsPending(state),
        fetchStagesSuccess: (
          state,
          payload: {
            data: Array<{ id: number; workflowAutomations: WorkflowAutomation[] }>;
            pipelineTemplateId: number;
          },
        ) =>
          pipeState(
            state,
            next =>
              createHydratedEntityState(
                next,
                payload.data.reduce(
                  (acc, stageAutomation) => {
                    const scopeId = stageIdScope(stageAutomation.id);
                    acc[scopeId] = stageAutomation.workflowAutomations;
                    return acc;
                  },
                  <Record<string, WorkflowAutomation[]>>{},
                ),
                adapter,
              ),
            next => adapter.markAsLoaded(next, { scope: null, entities: [] }),
          ),
        fetchStagesFailed: defaultActions.fetchCollectionFailed,
        fetchOneStage: (
          state,
          payload: { pipelineTemplateId: number; stageId: number; scope: string },
        ) => adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
        fetchOneStageSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchOneStageFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationCreatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            data: WorkflowAutomationUpdatePayload;
            additionalData: WorkflowAutomationAdditionalData;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (
          state,
          payload: { data: number; additionalData: WorkflowAutomationAdditionalData },
        ) => defaultActions.removeOnePessimistic(state, payload),
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
        fetchAutomationTemplates: ngrxEvent<{ id: number }>(),
        fetchAutomationTemplatesSuccess: ngrxEvent<AutomationTemplate[]>(),
        fetchAutomationTemplatesFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const PipelineTemplateStageAutomationsActions: PickActions<
  typeof actions,
  | 'fetchStages'
  | 'fetchOneStage'
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
  | 'fetchOptions'
  | 'fetchAutomationTemplates'
> = actions;

export {
  PipelineTemplateStageAutomationsActions,
  namespace as PipelineTemplateStageAutomationsNamespace,
  reducer as PipelineTemplateStageAutomationsReducer,
  selectors as PipelineTemplateStageAutomationsSelectors,
};
